/**
 * Token管理工具
 * 负责token的存储、获取、清除等操作
 */

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRE_TIME_KEY = 'token_expire_time';

/**
 * 存储访问令牌
 */
export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

/**
 * 获取访问令牌
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * 存储刷新令牌
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

/**
 * 获取刷新令牌
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Token有效期限制（秒）
 * 可根据需要调整：1小时=3600，2小时=7200，4小时=14400
 */
const MAX_TOKEN_EXPIRE_TIME = 7200; // 2小时

/**
 * 存储token过期时间
 */
export function setTokenExpireTime(expiresIn: number): void {
  expiresIn = Math.min(expiresIn, MAX_TOKEN_EXPIRE_TIME);
  const expireTime = Date.now() + expiresIn * 1000;
  localStorage.setItem(TOKEN_EXPIRE_TIME_KEY, expireTime.toString());
}

/**
 * 获取token过期时间
 */
export function getTokenExpireTime(): number | null {
  const expireTime = localStorage.getItem(TOKEN_EXPIRE_TIME_KEY);
  return expireTime ? parseInt(expireTime, 10) : null;
}

/**
 * 检查token是否即将过期（3分钟内）
 */
export function isTokenExpiringSoon(): boolean {
  const expireTime = getTokenExpireTime();
  if (!expireTime) return false;

  const timeLeft = expireTime - Date.now();
  const threeMinutes = 3 * 60 * 1000;

  return timeLeft < threeMinutes && timeLeft > 0;
}

/**
 * 检查token是否已过期
 */
export function isTokenExpired(): boolean {
  const expireTime = getTokenExpireTime();
  if (!expireTime) return false;

  return Date.now() >= expireTime;
}

/**
 * 存储所有token信息
 */
export function setTokens(
  accessToken: string,
  refreshToken: string,
  expiresIn?: number,
): void {
  setAccessToken(accessToken);
  setRefreshToken(refreshToken);
  if (expiresIn) {
    setTokenExpireTime(expiresIn);
  }
}

/**
 * 清除所有token信息
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRE_TIME_KEY);
}

/**
 * 解析JWT Token（不验证签名，仅解析payload）
 */
export function parseJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
        })
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}
